import React from "react";
import useSWR from "swr";
import { baseBackendUrl } from "../service/baseService";
import { useParams, Link as RouterLink } from "react-router-dom";
import { UserPage } from "../models/UserPage";
import { PageUpdate } from "../models/PageUpdate";
import { Box, Container, Breadcrumbs, Link, Typography, Skeleton, Alert } from "@mui/material";
import Parser from "html-react-parser";
import Linkify from "linkify-react";
import { getPageUpdates, getSplashImageSrc, getUpdateImageSrc, getVideoSrc } from "../service/UserPageService";
import { editorial, cardTexture } from "../common/editorialTheme";
import { parseUtc, postedAgo } from "../common/relativeTime";

const CustomPage: React.FC = () => {
  let { id } = useParams<string>();

  const { data, error, isLoading } = useSWR<UserPage>(
    id ? `${baseBackendUrl}/api/PageByShortName?shortName=${id}` : null,
    (url: string) => {
      return fetch(url).then(async (r) => {
        if (!r.ok) throw new Error("Fetch failed: " + r.status);
        const text = await r.text();
        try {
          return JSON.parse(text);
        } catch (e) {
          console.error("JSON Parse failed", text);
          throw e;
        }
      });
    }
  );

  const { data: updates } = useSWR<PageUpdate[]>(
    data?.id ? ["pageUpdates", data.id] : null,
    () => getPageUpdates(data!.id)
  );

  if (error) {
    return (
      <Box sx={{ backgroundColor: editorial.canvas, minHeight: "100vh", pt: 10 }}>
        <Container maxWidth="md">
          <Alert severity="error">Failed to load page content.</Alert>
        </Container>
      </Box>
    );
  }

  if (isLoading || !data) {
    return (
      <Box sx={{ backgroundColor: editorial.canvas, minHeight: "100vh", pt: 10, pb: 10 }}>
        <Container maxWidth="md">
          <Skeleton variant="rectangular" height={120} sx={{ mb: 3, borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
        </Container>
      </Box>
    );
  }

  const splashSrc = getSplashImageSrc(data.splashImageUrl);
  const videoSrc = getVideoSrc(data.videoUrl);

  const cardSx = {
    p: { xs: 3, md: 5 },
    mb: 4,
    borderRadius: "8px",
    backgroundColor: editorial.paper,
    backgroundImage: cardTexture,
    border: `1px solid ${editorial.divider}`,
    boxShadow: editorial.cardShadow,
  };

  return (
    <Box sx={{ backgroundColor: editorial.canvas, minHeight: "100vh", pb: 10 }}>
      <Container maxWidth="md" sx={{ pt: { xs: 4, md: 6 } }}>
        {/* Editorial header. An uploaded splash graphic becomes its backdrop; otherwise it's a plain card. */}
        <Box
          sx={{
            ...cardSx,
            position: "relative",
            overflow: "hidden",
            ...(splashSrc
              ? {
                  backgroundImage: `linear-gradient(rgba(12, 18, 17, 0.35), rgba(12, 18, 17, 0.6)), url("${splashSrc}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }
              : {}),
            minHeight: splashSrc ? 260 : undefined,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <Breadcrumbs
            separator="/"
            sx={{
              mb: 1.5,
              fontFamily: editorial.bodyFont,
              fontSize: "0.8rem",
              color: splashSrc ? "rgba(255,255,255,0.85)" : editorial.secondary,
              "& .MuiBreadcrumbs-separator": { color: splashSrc ? "rgba(255,255,255,0.5)" : editorial.divider },
            }}
          >
            <Link
              component={RouterLink}
              to="/"
              underline="hover"
              sx={{ color: splashSrc ? "rgba(255,255,255,0.85)" : editorial.secondary }}
            >
              Home
            </Link>
            <Typography
              sx={{
                fontFamily: editorial.bodyFont,
                fontSize: "0.8rem",
                color: splashSrc ? "#fff" : editorial.text,
                fontWeight: 600,
              }}
            >
              {data.title}
            </Typography>
          </Breadcrumbs>
          <Typography
            component="h1"
            sx={{
              fontFamily: editorial.headlineFont,
              fontWeight: 600,
              color: splashSrc ? "#fff" : editorial.primary,
              fontSize: { xs: "2rem", md: "2.75rem" },
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
              textShadow: splashSrc ? "0 2px 6px rgba(0,0,0,0.4)" : "none",
            }}
          >
            {data.title}
          </Typography>
        </Box>

        {/* Main content */}
        <Box
          sx={{
            ...cardSx,
            fontFamily: editorial.bodyFont,
            color: editorial.text,
            "& h1, & h2, & h3": {
              fontFamily: editorial.headlineFont,
              color: editorial.primary,
              fontWeight: 600,
              mt: 3,
              mb: 2,
            },
            "& p": { lineHeight: 1.7, mb: 2, color: editorial.textVariant, fontSize: "1.1rem" },
            "& ul, & ol": { mb: 2, pl: 4 },
            "& img": { maxWidth: "100%", height: "auto", borderRadius: "8px", my: 2, boxShadow: editorial.cardShadow },
            "& blockquote": {
              borderLeft: `3px solid ${editorial.primary}`,
              pl: 2,
              py: 1,
              my: 3,
              bgcolor: editorial.sectionAlt,
              fontFamily: editorial.headlineFont,
              fontStyle: "italic",
              color: editorial.primary,
            },
          }}
        >
          <Linkify options={{ target: "_blank" }}>{Parser(data.description)}</Linkify>
        </Box>

        {/* Updates */}
        {updates && updates.length > 0 && (
          <Box sx={cardSx}>
            <Typography
              component="h2"
              sx={{
                fontFamily: editorial.headlineFont,
                fontWeight: 600,
                color: editorial.primary,
                fontSize: "1.5rem",
                mb: 3,
              }}
            >
              Updates
            </Typography>
            {updates.map((update, index) => (
              <Box
                key={update.id}
                sx={{
                  py: 2.5,
                  borderBottom: index < updates.length - 1 ? `1px solid ${editorial.divider}` : "none",
                }}
              >
                <Typography sx={{ fontFamily: editorial.bodyFont, fontWeight: 600, color: editorial.text, fontSize: "1.05rem" }}>
                  {update.title}
                </Typography>
                <Typography
                  title={update.createdAt ? parseUtc(update.createdAt).toLocaleString() : undefined}
                  sx={{
                    fontFamily: editorial.bodyFont,
                    color: editorial.secondary,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    mt: 0.5,
                    mb: 1,
                  }}
                >
                  {update.createdAt ? postedAgo(update.createdAt) : ""}
                </Typography>
                <Box
                  sx={{
                    fontFamily: editorial.bodyFont,
                    "& p": { lineHeight: 1.7, color: editorial.textVariant, m: 0 },
                  }}
                >
                  <Linkify options={{ target: "_blank" }}>{Parser(update.body)}</Linkify>
                </Box>
                {update.images && update.images.length > 0 && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: update.images.length === 1 ? "1fr" : { xs: "1fr 1fr", md: "repeat(3, 1fr)" },
                      gap: 1.5,
                      mt: 2,
                    }}
                  >
                    {update.images.map((image) => {
                      const src = getUpdateImageSrc(image.url);
                      return (
                        <Box
                          key={image.id}
                          component="a"
                          href={src}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: "block", borderRadius: "8px", overflow: "hidden", boxShadow: editorial.cardShadow }}
                        >
                          <Box
                            component="img"
                            src={src}
                            alt={update.title}
                            loading="lazy"
                            sx={{
                              width: "100%",
                              display: "block",
                              objectFit: "cover",
                              ...(update.images!.length === 1 ? { maxHeight: 480 } : { aspectRatio: "4 / 3" }),
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Video, on its own card below the updates */}
        {videoSrc && (
          <Box sx={cardSx}>
            <Typography
              component="h2"
              sx={{
                fontFamily: editorial.headlineFont,
                fontWeight: 600,
                color: editorial.primary,
                fontSize: "1.5rem",
                mb: 3,
              }}
            >
              Video
            </Typography>
            <Box
              component="video"
              src={videoSrc}
              controls
              sx={{ width: "100%", maxHeight: 480, borderRadius: "8px", backgroundColor: "#000", display: "block" }}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default CustomPage;
